import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut, type User } from 'firebase/auth'
import {
  collection, doc, limit, onSnapshot, query, updateDoc, where, serverTimestamp, type Timestamp,
} from 'firebase/firestore'
import { auth, db, googleProvider, ALLOWED_DOMAIN } from './firebase'
import type { Scope, StudentDoc, UserDoc } from './types'
import { isStudentEmail } from './students'
import { PREVIEW_ROLES, PREVIEW_TOOLS, previewRole, setPreviewRole, previewStudent, type PreviewRole } from './preview'
import { holds, holdsAnyForTool, holdsRole, ROLES, scopesFor, administrableScopes, roleLabels, type Cap } from './caps'

type Status = 'loading' | 'signed-out' | 'wrong-domain' | 'not-on-roster' | 'ready'

interface AuthState {
  status: Status
  user: User | null
  /** A staff member's access record. Null for a student. */
  profile: UserDoc | null
  /**
   * A student's roster entry. Null for staff. Students and staff are different
   * kinds of account — never both — decided by whether the address starts
   * with a digit, the same line firestore.rules draws.
   */
  student: StudentDoc | null
  /** Whoever is signed in, whichever kind — for greetings and the menu. */
  displayName: string
  /** Named as somebody's seminar teacher on the student roster. Derived, never granted. */
  isSeminarTeacher: boolean
  /**
   * "View as…": the role a district admin is previewing, or null. While set,
   * profile / student / can / isSeminarTeacher all describe that role instead
   * of you, and every write is refused (see ./preview.ts and ./db.ts).
   */
  preview: PreviewRole | null
  /** Whether this person may preview at all, by their real record. */
  canPreview: boolean
  /** The tools this person may preview: a tool's own admins preview it; district admins preview every tool. */
  previewTools: string[]
  setPreview: (r: PreviewRole | null) => void
  /** Why the last sign-in attempt failed, in words a person can act on. */
  signInError: string | null
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
  /**
   * Every permission decision in the UI goes through here.
   *
   *   can('norseterm.teach')          — anywhere
   *   can('norseterm.review', 'hs')   — at the high school
   *
   * This is convenience, not security. firestore.rules asks the same questions
   * of the same field and is the thing that actually holds.
   */
  can: (cap: Cap, scope?: Scope) => boolean
  /** Any capability at all for this tool — the question a tile asks. */
  canUseTool: (tool: string) => boolean
  /** Every scope where this person holds `cap` — drives scope pickers. */
  scopes: (cap: Cap) => Scope[]
  /** Scopes this person may hand out permissions in. Empty for most people. */
  adminScopes: () => Scope[]
}

const Ctx = createContext<AuthState | null>(null)

/** How stale lastSeenAt may get before it is re-stamped. An hour keeps it
 *  useful ("never signed in", "not since August") for almost no writes. */
const SEEN_EVERY_MS = 60 * 60 * 1000
let stampedThisSession = false

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserDoc | null>(null)
  const [student, setStudent] = useState<StudentDoc | null>(null)
  const [isSeminarTeacher, setIsSeminarTeacher] = useState(false)
  const [preview, setPreviewState] = useState<PreviewRole | null>(previewRole())
  const [status, setStatus] = useState<Status>('loading')
  const [signInError, setSignInError] = useState<string | null>(null)

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUser(u)
    if (!u) { setProfile(null); setStudent(null); setStatus('signed-out'); return }
    if (!u.email?.toLowerCase().endsWith('@' + ALLOWED_DOMAIN)) {
      setStatus('wrong-domain'); return
    }
    setStatus('loading')
  }), [])

  useEffect(() => {
    if (!user || status === 'wrong-domain') return
    // Live subscription rather than a one-time read: revoking someone's access
    // takes effect in their open session, not an hour later when the token
    // happens to refresh.
    // Keyed by lowercased email so an admin can set somebody up before their
    // first sign-in — see firestore.rules.
    const email = user.email?.toLowerCase()
    if (!email) { setProfile(null); setStatus('not-on-roster'); return }
    // A student address looks itself up on the student roster, never in
    // /users — the rules would refuse it there anyway.
    const kind = isStudentEmail(email) ? 'students' : 'users'
    return onSnapshot(
      doc(db, kind, email),
      (snap) => {
        if (!snap.exists() || snap.data()?.active !== true) {
          setProfile(null); setStudent(null); setStatus('not-on-roster'); return
        }
        const data = snap.data() as UserDoc & StudentDoc
        if (kind === 'students') { setStudent(data); setProfile(null) }
        else { setProfile(data); setStudent(null) }
        setStatus('ready')
        const seen = (data.lastSeenAt as Timestamp | undefined)?.toMillis?.() ?? 0
        if (!stampedThisSession && Date.now() - seen > SEEN_EVERY_MS) {
          stampedThisSession = true
          // Best effort: a failed stamp must never get in the way of signing in.
          updateDoc(snap.ref, { lastSeenAt: serverTimestamp() }).catch(() => {})
        }
      },
      () => { setProfile(null); setStudent(null); setStatus('not-on-roster') },
    )
  }, [user, status])

  // Seminar teachers are whoever the student roster names — one cheap query,
  // not a capability anybody grants. Only asked of staff.
  const staffEmail = profile ? user?.email?.toLowerCase() : undefined
  useEffect(() => {
    if (!staffEmail) { setIsSeminarTeacher(false); return }
    return onSnapshot(
      query(collection(db, 'students'), where('seminarTeacherEmail', '==', staffEmail), limit(1)),
      (s) => setIsSeminarTeacher(!s.empty),
      () => setIsSeminarTeacher(false),
    )
  }, [staffEmail])

  // Decided by the *real* record, so a preview can never grant itself the
  // right to keep previewing, and a preview left in sessionStorage by somebody
  // else on a shared machine does nothing for a non-admin. Per tool: that
  // tool's admins (who can already read everything its roles are shown), and
  // district admins for every tool.
  const previewTools = PREVIEW_TOOLS.filter((t) => {
    if (profile?.districtAdmin) return true
    const admin = ROLES.find((r) => r.id === t.adminRole)
    return !!profile && profile.active !== false && !!admin && holdsRole(profile.caps, admin, t.scope)
  }).map((t) => t.tool)
  const canPreview = previewTools.length > 0
  const role = PREVIEW_ROLES.find((r) => r.k === preview && previewTools.includes(r.tool))
  useEffect(() => {
    if (status === 'ready' && preview && !role) { setPreviewRole(null); setPreviewState(null) }
  }, [status, preview, role])

  const realName = profile?.displayName ?? student?.displayName ?? user?.displayName ?? ''
  const effProfile: UserDoc | null = role
    ? (role.student ? null : { ...profile!, districtAdmin: false, caps: role.caps })
    : profile
  const effStudent: StudentDoc | null = role
    ? (role.student ? previewStudent((user?.email ?? '').toLowerCase(), realName) : null)
    : student
  const effSeminar = role ? !!role.seminar : isSeminarTeacher

  const value = useMemo<AuthState>(() => ({
    status, user, profile: effProfile, student: effStudent, signInError, isSeminarTeacher: effSeminar,
    preview: role?.k ?? null, canPreview, previewTools,
    setPreview: (r) => { setPreviewRole(r); setPreviewState(r) },
    displayName: realName,
    // A button that does nothing when it fails is the worst possible outcome:
    // the person has no idea whether to retry, switch accounts, or call someone.
    signIn: async () => {
      setSignInError(null)
      try {
        // Popup in an ordinary browser tab, everywhere: the result comes back
        // over postMessage while the popup is open, with no storage read
        // after a fresh page load. Redirect depended on that read, and iPhone
        // browsers (Chrome on iOS especially) partition or drop the storage
        // in between: "Unable to process request due to missing initial
        // state". Redirect is kept only where a popup cannot work, an
        // installed home-screen app, and as the fallback if a popup is
        // blocked.
        const installed = window.matchMedia?.('(display-mode: standalone)').matches
          || (navigator as Navigator & { standalone?: boolean }).standalone === true
        if (installed) {
          await signInWithRedirect(auth, googleProvider)
        } else {
          try {
            await signInWithPopup(auth, googleProvider)
          } catch (e) {
            const code = (e as { code?: string }).code ?? ''
            if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
              await signInWithRedirect(auth, googleProvider)
            } else throw e
          }
        }
      } catch (e) {
        setSignInError(explainAuthError(e))
      }
    },
    signOutNow: () => signOut(auth),
    can: (cap, scope) => {
      if (!effProfile) return false
      // The one bypass, and it is deliberate: somebody has to be able to fix a
      // permissions mistake at 7am without another admin being awake.
      if (effProfile.districtAdmin) return true
      return holds(effProfile.caps, cap, scope)
    },
    // A student's whole app is NorseTerm. A seminar teacher reaches it through
    // the roster even with no NorseTerm capability of their own.
    canUseTool: (tool) => (effStudent ? tool === 'norseterm'
      : !!effProfile && (effProfile.districtAdmin || holdsAnyForTool(effProfile.caps, tool)
        || (tool === 'norseterm' && effSeminar))),
    scopes: (cap) => scopesFor(effProfile?.caps, cap),
    adminScopes: () => administrableScopes(effProfile ?? {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [status, user, profile, student, signInError, isSeminarTeacher, preview, canPreview, previewTools.join(',')])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>')
  return v
}

/**
 * Firebase error codes are useless to the person holding the phone. Translate
 * the ones that actually happen into a next action.
 */
function explainAuthError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? ''
  switch (code) {
    case 'auth/invalid-api-key':
    case 'auth/api-key-not-valid':
      return 'This app is misconfigured (bad API key). Nothing you can fix. Send this message to whoever set it up.'
    case 'auth/operation-not-allowed':
      return 'Google sign-in is not switched on for this project yet.'
    case 'auth/unauthorized-domain':
      return `This web address is not on the approved list for sign-in (${location.hostname}).`
    case 'auth/network-request-failed':
      return 'Could not reach Google. Check your connection and try again.'
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.'
    case 'auth/popup-closed-by-user':
      return 'The sign-in window was closed before finishing. Try again.'
    default:
      return code
        ? `Sign-in failed: ${code}`
        : `Sign-in failed: ${e instanceof Error ? e.message : 'unknown error'}`
  }
}

/**
 * What to call the signed-in person in one tool: their capability roles, plus
 * the roles the student roster gives them — Student, or Seminar teacher.
 */
export function myRoleLabels(a: Pick<AuthState, 'profile' | 'student' | 'isSeminarTeacher'>, tool: string): string[] {
  if (a.student) return tool === 'norseterm' ? ['Student'] : []
  const base = roleLabels(a.profile, tool)
  if (tool === 'norseterm' && a.isSeminarTeacher && !a.profile?.districtAdmin) {
    return [...base, 'Seminar teacher']
  }
  return base
}
